/**
 * Map static-content `/searchCityAirport/v2` (or allAirports) rows
 * into the shared option shape used by GiGi B2B + B2C.
 *
 * Host owns HTTP; this only normalizes the payload.
 *
 * Hierarchy: v2 may already send isChild/parentCode. Full-list
 * allAirports does not — derive from type=city + otherAirportCodes
 * (e.g. CDG.otherAirportCodes = ['PAR']).
 */

/** Postgres may send text[], JSON, "{YTO}", or "YTO,YTZ". */
export function parseAirportCodeList(value) {
  if (value == null || value === '') {
    return [];
  }
  if (Array.isArray(value)) {
    return value.flatMap((item) => parseAirportCodeList(item)).filter(Boolean);
  }
  const raw = String(value).trim();
  if (!raw || raw === 'null' || raw === '[]' || raw === '{}') {
    return [];
  }
  if (raw.startsWith('[')) {
    try {
      return parseAirportCodeList(JSON.parse(raw));
    } catch {
      // fall through to delimiter split
    }
  }
  return raw
    .replace(/^[{\[]|[}\]]$/g, '')
    .split(/[,;|]/)
    .map((code) => code.replace(/^["']|["']$/g, '').trim().toUpperCase())
    .filter((code) => /^[A-Z0-9]{3,4}$/.test(code));
}

function isCityType(element) {
  return String(element?.type || '').toLowerCase() === 'city';
}

function isMetroRecord(element) {
  return (
    isCityType(element) ||
    /all\s*airports/i.test(String(element?.name || ''))
  );
}

/** Child airports point at a metro (YYZ → YTO). City rows may list children. */
export function deriveAirportParentCode(element) {
  if (!element) return null;
  if (element.parentCode) {
    return String(element.parentCode).toUpperCase();
  }
  if (isCityType(element)) {
    return null;
  }
  const codes = parseAirportCodeList(element.otherAirportCodes);
  return codes[0] || null;
}

function countryCode(option) {
  const value = option?.value || {};
  return String(value.countrycode || value.country_code || '').toUpperCase();
}

function sortRankBucket(matches) {
  return [...matches].sort((a, b) => {
    const countryA = countryCode(a);
    const countryB = countryCode(b);
    if (countryA !== countryB) {
      return countryA.localeCompare(countryB);
    }
    return String(a.value?.code || '').localeCompare(String(b.value?.code || ''));
  });
}

/** Mirrors static-content buildGroupedAirportSuggest matchRank. */
function matchRankOption(option, str) {
  const value = option?.value || {};
  const code = String(value.code || '').toUpperCase();
  const city = String(value.city || '').toUpperCase();
  const name = String(value.name || '').toUpperCase();
  const location = String(value.location || '').toUpperCase();
  if (code.startsWith(str)) {
    return 1;
  }
  if (city.startsWith(str) || location.startsWith(str)) {
    return 2;
  }
  if (name.startsWith(str)) {
    return 3;
  }
  if (name.includes(str) || city.includes(str) || location.includes(str)) {
    return 4;
  }
  return null;
}

export function mapSearchCityAirportResponse(result) {
  if (!result) {
    return [];
  }

  const rows = Array.isArray(result)
    ? result
    : Array.isArray(result.airports)
      ? result.airports
      : [];

  return rows.map((element) => {
    const derivedParent = deriveAirportParentCode(element);
    const isChild =
      element.isChild === true ||
      element.isChild === 'true' ||
      Boolean(derivedParent && !isCityType(element));

    return {
      value: element,
      label: `${element.code} - ${element.name}`,
      customAbbreviation: element.location,
      isChild: Boolean(isChild),
      parentCode: isChild ? derivedParent : null,
    };
  });
}

/** Unique option identity when the same IATA can appear as parent + child. */
export function getAirportSuggestOptionValue(option) {
  const code = option?.value?.code || '';
  const kind = option?.isChild ? 'child' : 'main';
  const parent = option?.parentCode || '';
  return `${code}-${kind}-${parent}`;
}

/**
 * MUI Autocomplete filter that preserves API rank/grouping.
 * Do not client-refilter away parent → child order from v2.
 */
export function preserveApiOrderFilter(options, state) {
  if (!options || options.length === 0) {
    return [];
  }
  const term = (state?.inputValue || '').trim();
  if (!term) {
    return [];
  }
  return options;
}

/**
 * Place metro parents first, each followed by their child airports.
 * Pulls missing parents/children from the full list when needed.
 */
export function groupAirportSuggestOptions(matches, allOptions, maxResults = 50) {
  if (!matches?.length) {
    return [];
  }

  const byCode = new Map();
  for (const option of allOptions || []) {
    const code = option?.value?.code?.toUpperCase();
    if (!code) continue;
    const existing = byCode.get(code);
    // Prefer non-child (metro/main) row when the same code appears twice.
    if (!existing || (existing.isChild && !option.isChild)) {
      byCode.set(code, option);
    }
  }

  const selected = new Map();
  const add = (option) => {
    if (!option) return;
    selected.set(getAirportSuggestOptionValue(option), option);
  };

  matches.forEach(add);

  // Ensure parents of matched children are present.
  for (const option of [...selected.values()]) {
    if (!option.isChild || !option.parentCode) continue;
    add(byCode.get(String(option.parentCode).toUpperCase()));
  }

  // Attach full-list children under any selected parent/metro.
  const parentCodes = new Set();
  for (const option of selected.values()) {
    if (option.isChild) continue;
    const code = option?.value?.code?.toUpperCase();
    if (code) parentCodes.add(code);
  }
  for (const option of allOptions || []) {
    if (!option?.isChild || !option.parentCode) continue;
    if (parentCodes.has(String(option.parentCode).toUpperCase())) {
      add(option);
    }
  }

  // City metros may list child IATA codes in otherAirportCodes.
  for (const option of [...selected.values()]) {
    if (option.isChild || !isCityType(option?.value)) continue;
    for (const code of parseAirportCodeList(option?.value?.otherAirportCodes)) {
      add(byCode.get(code));
    }
  }

  const combined = [];
  const seen = new Set();
  const push = (option) => {
    if (!option || combined.length >= maxResults) return;
    const key = getAirportSuggestOptionValue(option);
    if (seen.has(key)) return;
    seen.add(key);
    combined.push(option);
  };

  const parents = [];
  const looseChildren = [];
  for (const option of selected.values()) {
    if (option.isChild) {
      looseChildren.push(option);
    } else {
      parents.push(option);
    }
  }

  for (const parent of parents) {
    push(parent);
    const parentCode = String(parent?.value?.code || '').toUpperCase();
    for (const option of selected.values()) {
      if (
        option.isChild &&
        String(option.parentCode || '').toUpperCase() === parentCode
      ) {
        push(option);
      }
    }
  }

  for (const option of looseChildren) {
    push(option);
  }

  return combined;
}

/**
 * Only keep the child arrow when the parent metro is also in the list.
 * Exact-code / v2 responses often return CDG alone with isChild=true —
 * without PAR that arrow looks wrong on a "main" row.
 */
export function normalizeAirportSuggestChildMarkers(options) {
  if (!options?.length) {
    return [];
  }
  const codes = new Set(
    options.map((option) => option?.value?.code?.toUpperCase()).filter(Boolean)
  );
  return options.map((option) => {
    const parent = option?.parentCode
      ? String(option.parentCode).toUpperCase()
      : null;
    // Arrow is only meaningful when the parent metro is also visible.
    if (option?.isChild && (!parent || !codes.has(parent))) {
      return { ...option, isChild: false };
    }
    return option;
  });
}

function mergeGroupedOptions(groups, maxResults) {
  const combined = [];
  const seen = new Set();
  for (const group of groups) {
    for (const option of group || []) {
      const key = getAirportSuggestOptionValue(option);
      if (seen.has(key)) continue;
      seen.add(key);
      combined.push(option);
      if (combined.length >= maxResults) {
        return combined;
      }
    }
  }
  return combined;
}

/**
 * Local filter matching static-content `/searchCityAirport/v2` processData,
 * then regroup into parent → child hierarchy for the UI.
 *
 * Matches city, name, location, and IATA. Exact IATA stays first so
 * "ORD" is not crowded out by city substrings like Oxford / Concord.
 *
 * @param {Array} allOptions
 * @param {string} query
 * @param {{ maxResults?: number }} [opts]
 */
export function filterAirportSuggestOptions(allOptions, query, opts = {}) {
  const maxResults = opts.maxResults ?? 50;
  const str = (query || '').trim().toUpperCase();
  if (!str || !Array.isArray(allOptions) || allOptions.length === 0) {
    return [];
  }

  const exact = [];
  const rankBuckets = [[], [], [], []];

  for (const option of allOptions) {
    const code = option?.value?.code?.toUpperCase();
    if (code && code === str) {
      exact.push(option);
      continue;
    }
    const rank = matchRankOption(option, str);
    if (rank != null) {
      rankBuckets[rank - 1].push(option);
    }
  }

  const groups = [];
  let remaining = maxResults;
  const pushGroup = (matches) => {
    if (remaining <= 0 || !matches.length) return;
    const grouped = groupAirportSuggestOptions(
      sortRankBucket(matches),
      allOptions,
      remaining
    );
    if (grouped.length) {
      groups.push(grouped);
      remaining -= grouped.length;
    }
  };

  if (exact.length && exact.every((option) => !isMetroRecord(option?.value))) {
    return normalizeAirportSuggestChildMarkers(
      sortRankBucket(exact).map((option) => ({
        ...option,
        isChild: false,
        parentCode: null,
      }))
    );
  }

  if (exact.length) {
    pushGroup(exact);
  }
  for (const bucket of rankBuckets) {
    pushGroup(bucket);
  }

  return normalizeAirportSuggestChildMarkers(
    mergeGroupedOptions(groups, maxResults)
  );
}
